import React, { useState, useEffect, useCallback } from 'react';
import {
  FiCpu, FiKey, FiEye, FiEyeOff, FiCheck, FiAlertCircle,
  FiZap, FiRefreshCw, FiSave, FiBarChart2, FiToggleLeft, FiToggleRight, FiSliders,
} from 'react-icons/fi';
import {
  getAiConfig, updateAiConfigKey, testAiConnection, getAiUsage,
  type AiConfigRow, type AiUsageStats,
} from '../../api/admin';

const ROUTING_TASKS = [
  // Phase 1
  { key: 'suggest-architecture',       label: 'Génération architecture',       phase: 1 },
  { key: 'discuss-architecture',       label: 'Discussion architecture',       phase: 1 },
  { key: 'refine-architecture',        label: 'Affinage architecture',         phase: 1 },
  { key: 'generate-terraform',         label: 'Génération Terraform',          phase: 1 },
  { key: 'generate-documentation',     label: 'Documentation technique',       phase: 1 },
  { key: 'adr-generation',             label: 'ADR & Décisions archi',         phase: 1 },
  // Phase 2
  { key: 'review-architecture',        label: 'Revue architecture (sécurité)', phase: 2 },
  { key: 'compare-variants',           label: 'Comparaison variantes',         phase: 2 },
  { key: 'chat-with-infra',            label: 'Chat infrastructure',           phase: 2 },
  { key: 'runbook-generation',         label: 'Runbooks opérationnels',        phase: 2 },
  { key: 'finops-recommendations',     label: 'Recommandations FinOps',        phase: 2 },
  { key: 'executive-summary',          label: 'Executive Summary',             phase: 2 },
  // Phase 3
  { key: 'compliance-scanner',         label: 'Scan de conformité',            phase: 3 },
  { key: 'migration-assistant',        label: 'Assistant migration',           phase: 3 },
  { key: 'presales-proposal',          label: 'Proposition commerciale',       phase: 3 },
  { key: 'incremental-update',         label: 'Mise à jour incrémentale',      phase: 3 },
  { key: 'drift-detection',            label: 'Drift Detection',               phase: 3 },
  { key: 'continuous-optimization',    label: 'Optimisation continue',         phase: 3 },
];
const CLAUDE_MODELS = [
  { id: 'claude-sonnet-4-6',         label: 'Claude Sonnet 4.6 — Équilibré' },
  { id: 'claude-opus-4-7',           label: 'Claude Opus 4.7 — Puissant' },
  { id: 'claude-haiku-4-5-20251001', label: 'Claude Haiku 4.5 — Rapide' },
];
const OPENAI_MODELS = [
  { id: 'gpt-4o',                    label: 'GPT-4o — Équilibré' },
  { id: 'gpt-4o-mini',               label: 'GPT-4o mini — Rapide' },
  { id: 'gpt-4-turbo',               label: 'GPT-4 Turbo — Puissant' },
  { id: 'gpt-4-turbo-preview',       label: 'GPT-4 Turbo Preview — Puissant' },
  { id: 'gpt-4',                     label: 'GPT-4 — Classique' },
  { id: 'gpt-3.5-turbo',             label: 'GPT-3.5 Turbo — Économique' },
  { id: 'o1',                        label: 'o1 — Raisonnement' },
  { id: 'o1-mini',                   label: 'o1 mini — Raisonnement rapide' },
  { id: 'o3-mini',                   label: 'o3 mini — Raisonnement avancé' },
];

const AdminAIConfig: React.FC = () => {
  const [configs, setConfigs]       = useState<AiConfigRow[]>([]);
  const [usage, setUsage]           = useState<AiUsageStats | null>(null);
  const [loading, setLoading]       = useState(true);
  const [saving, setSaving]         = useState<string | null>(null);
  const [success, setSuccess]       = useState<string | null>(null);
  const [error, setError]           = useState<string | null>(null);
  const [showKey, setShowKey]       = useState(false);
  const [apiKeyDraft, setApiKeyDraft] = useState('');
  const [testing, setTesting]         = useState<'claude' | 'openai' | null>(null);
  const [testResult, setTestResult]   = useState<Record<string, { success: boolean; model?: string; latencyMs?: number; error?: string }>>({});
  const [openaiKeyDraft, setOpenaiKeyDraft] = useState('');
  const [showOpenaiKey, setShowOpenaiKey]   = useState(false);

  const getVal = useCallback((key: string) =>
    configs.find(c => c.key === key)?.value ?? '', [configs]);

  const getBool = useCallback((key: string) =>
    getVal(key) === 'true', [getVal]);

  const load = async () => {
    setLoading(true);
    try {
      const [cfgRes, usageRes] = await Promise.all([getAiConfig(), getAiUsage()]);
      setConfigs(cfgRes.configs);
      setUsage(usageRes);
    } catch {
      setError('Erreur de chargement');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const flash = (msg: string) => {
    setSuccess(msg);
    setTimeout(() => setSuccess(null), 3000);
  };

  const save = async (key: string, value: string | boolean | number) => {
    setSaving(key);
    setError(null);
    try {
      await updateAiConfigKey(key, value);
      await load();
      flash('Sauvegardé');
    } catch (e: any) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setSaving(null);
    }
  };

  const saveApiKey = () => {
    if (!apiKeyDraft.trim()) return;
    save('claude_api_key', apiKeyDraft.trim());
    setApiKeyDraft('');
  };

  const handleTest = async (provider: 'claude' | 'openai') => {
    setTesting(provider);
    setTestResult(prev => ({ ...prev, [provider]: undefined as any }));
    try {
      const res = await testAiConnection(provider);
      setTestResult(prev => ({ ...prev, [provider]: res }));
    } catch (e: any) {
      const serverMsg = e.response?.data?.error;
      const isTimeout = e.code === 'ECONNABORTED' || e.message?.includes('timeout');
      const errMsg = serverMsg || (isTimeout ? 'Timeout' : e.message);
      setTestResult(prev => ({ ...prev, [provider]: { success: false, error: errMsg } }));
    } finally {
      setTesting(null);
    }
  };

  const saveOpenaiKey = () => {
    if (!openaiKeyDraft.trim()) return;
    save('openai_api_key', openaiKeyDraft.trim());
    setOpenaiKeyDraft('');
  };

  // Routing rules helpers
  const getRoutingRules = useCallback((): Record<string, { provider: string; model: string }> => {
    const raw = getVal('routing_rules');
    try { return raw ? JSON.parse(raw) : {}; } catch { return {}; }
  }, [getVal]);

  const updateRoutingRule = (taskKey: string, field: 'provider' | 'model', value: string) => {
    const rules = getRoutingRules();
    rules[taskKey] = { ...rules[taskKey], [field]: value };
    save('routing_rules', JSON.stringify(rules));
  };

  const isClaudeConfigured = getVal('claude_api_key') === '••••••••';
  const isOpenaiConfigured = getVal('openai_api_key') === '••••••••';
  const isConfigured = isClaudeConfigured; // backward compat

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <FiRefreshCw className="animate-spin text-blue-600 text-2xl mr-2" />
      <span className="text-gray-600">Chargement...</span>
    </div>
  );

  return (
    <div className="max-w-3xl mx-auto space-y-6">

      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <FiCpu className="text-blue-600 text-2xl" />
          <div>
            <h1 className="text-xl font-bold text-gray-900">Configuration IA</h1>
            <p className="text-sm text-gray-500">LLM Provider · Phases · Quotas</p>
          </div>
        </div>
        <button onClick={load} className="p-2 rounded-lg hover:bg-gray-100" title="Rafraîchir">
          <FiRefreshCw className="text-gray-500" />
        </button>
      </div>

      {/* Alerts */}
      {success && (
        <div className="flex items-center space-x-2 bg-green-50 border border-green-200 text-green-700 rounded-lg px-4 py-2 text-sm">
          <FiCheck /> <span>{success}</span>
        </div>
      )}
      {error && (
        <div className="flex items-center space-x-2 bg-red-50 border border-red-200 text-red-700 rounded-lg px-4 py-2 text-sm">
          <FiAlertCircle /> <span>{error}</span>
        </div>
      )}

      {/* ── Provider Claude ─────────────────────────────────────────── */}
      <section className="bg-white rounded-xl border border-gray-200 divide-y divide-gray-100">
        <div className="px-5 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 bg-orange-100 rounded-lg flex items-center justify-center">
              <FiCpu className="text-orange-600" />
            </div>
            <div>
              <p className="font-semibold text-gray-900">Claude (Anthropic)</p>
              <p className="text-xs text-gray-500">Provider LLM principal</p>
            </div>
          </div>
          <button
            onClick={() => save('claude_enabled', !getBool('claude_enabled'))}
            disabled={saving === 'claude_enabled'}
            className="flex items-center space-x-1.5 text-sm font-medium"
          >
            {getBool('claude_enabled')
              ? <><FiToggleRight className="text-blue-600 text-xl" /><span className="text-blue-600">Actif</span></>
              : <><FiToggleLeft className="text-gray-400 text-xl" /><span className="text-gray-400">Inactif</span></>
            }
          </button>
        </div>

        {/* Clé API */}
        <div className="px-5 py-4 space-y-3">
          <label className="block text-sm font-medium text-gray-700">
            <FiKey className="inline mr-1" />Clé API
          </label>
          <div className="flex items-center space-x-2">
            <div className="relative flex-1">
              <input
                type={showKey ? 'text' : 'password'}
                value={apiKeyDraft}
                onChange={e => setApiKeyDraft(e.target.value)}
                placeholder={isConfigured ? '••••••••  (clé déjà configurée)' : 'sk-ant-...'}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 pr-10 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              <button
                type="button"
                onClick={() => setShowKey(v => !v)}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                {showKey ? <FiEyeOff /> : <FiEye />}
              </button>
            </div>
            <button
              onClick={saveApiKey}
              disabled={!apiKeyDraft.trim() || saving === 'claude_api_key'}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-1"
            >
              {saving === 'claude_api_key' ? <FiRefreshCw className="animate-spin" /> : <FiSave />}
              <span>Sauvegarder</span>
            </button>
          </div>
          {isConfigured && (
            <p className="text-xs text-green-600 flex items-center space-x-1">
              <FiCheck /><span>Clé configurée et chiffrée en base</span>
            </p>
          )}
        </div>

        {/* Modèle par défaut */}
        <div className="px-5 py-4 flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-gray-700">Modèle par défaut</p>
            <p className="text-xs text-gray-500">{getVal('claude_default_model') || 'claude-sonnet-4-6'}</p>
          </div>
          <select
            value={getVal('claude_default_model') || 'claude-sonnet-4-6'}
            onChange={e => save('claude_default_model', e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:ring-2 focus:ring-blue-500"
          >
            {CLAUDE_MODELS.map(m => <option key={m.id} value={m.id}>{m.label}</option>)}
          </select>
        </div>

        {/* Test connexion Claude */}
        <div className="px-5 py-4">
          <div className="flex items-center space-x-3">
            <button
              onClick={() => handleTest('claude')}
              disabled={testing !== null || !isClaudeConfigured}
              className="flex items-center space-x-2 px-4 py-2 border border-blue-600 text-blue-600 rounded-lg text-sm font-medium hover:bg-blue-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {testing === 'claude' ? <FiRefreshCw className="animate-spin" /> : <FiZap />}
              <span>{testing === 'claude' ? 'Test en cours...' : 'Tester la connexion'}</span>
            </button>
            {!isClaudeConfigured && <p className="text-xs text-amber-600">Configurez d'abord la clé API</p>}
          </div>
          {testResult.claude && (
            <div className={`mt-3 flex items-center space-x-2 text-sm rounded-lg px-3 py-2 ${testResult.claude.success ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
              {testResult.claude.success ? <FiCheck /> : <FiAlertCircle />}
              <span>{testResult.claude.success ? `Connexion OK — ${testResult.claude.model} — ${testResult.claude.latencyMs}ms` : testResult.claude.error}</span>
            </div>
          )}
        </div>
      </section>

      {/* ── Provider OpenAI ─────────────────────────────────────────── */}
      <section className="bg-white rounded-xl border border-gray-200 divide-y divide-gray-100">
        <div className="px-5 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center">
              <span className="text-green-700 font-bold text-xs">GPT</span>
            </div>
            <div>
              <p className="font-semibold text-gray-900">OpenAI (GPT-4o)</p>
              <p className="text-xs text-gray-500">Provider narratif — Executive Summary, Runbooks, FinOps</p>
            </div>
          </div>
          <button
            onClick={() => save('openai_enabled', !getBool('openai_enabled'))}
            disabled={saving === 'openai_enabled'}
            className="flex items-center space-x-1.5 text-sm font-medium"
          >
            {getBool('openai_enabled')
              ? <><FiToggleRight className="text-green-600 text-xl" /><span className="text-green-600">Actif</span></>
              : <><FiToggleLeft className="text-gray-400 text-xl" /><span className="text-gray-400">Inactif</span></>}
          </button>
        </div>

        {/* Clé API OpenAI */}
        <div className="px-5 py-4 space-y-3">
          <label className="block text-sm font-medium text-gray-700"><FiKey className="inline mr-1" />Clé API</label>
          <div className="flex items-center space-x-2">
            <div className="relative flex-1">
              <input
                type={showOpenaiKey ? 'text' : 'password'}
                value={openaiKeyDraft}
                onChange={e => setOpenaiKeyDraft(e.target.value)}
                placeholder={isOpenaiConfigured ? '••••••••  (clé déjà configurée)' : 'sk-...'}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 pr-10 text-sm focus:ring-2 focus:ring-green-500 focus:border-transparent"
              />
              <button type="button" onClick={() => setShowOpenaiKey(v => !v)} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                {showOpenaiKey ? <FiEyeOff /> : <FiEye />}
              </button>
            </div>
            <button onClick={saveOpenaiKey} disabled={!openaiKeyDraft.trim() || saving === 'openai_api_key'}
              className="px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 disabled:opacity-50 flex items-center space-x-1">
              {saving === 'openai_api_key' ? <FiRefreshCw className="animate-spin" /> : <FiSave />}
              <span>Sauvegarder</span>
            </button>
          </div>
          {isOpenaiConfigured && <p className="text-xs text-green-600 flex items-center space-x-1"><FiCheck /><span>Clé configurée et chiffrée en base</span></p>}
        </div>

        {/* Modèle OpenAI */}
        <div className="px-5 py-4 flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-gray-700">Modèle par défaut</p>
            <p className="text-xs text-gray-500">{getVal('openai_default_model') || 'gpt-4o'}</p>
          </div>
          <select value={getVal('openai_default_model') || 'gpt-4o'} onChange={e => save('openai_default_model', e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:ring-2 focus:ring-green-500">
            {OPENAI_MODELS.map(m => <option key={m.id} value={m.id}>{m.label}</option>)}
          </select>
        </div>

        {/* Test connexion OpenAI */}
        <div className="px-5 py-4">
          <div className="flex items-center space-x-3">
            <button onClick={() => handleTest('openai')} disabled={testing !== null || !isOpenaiConfigured}
              className="flex items-center space-x-2 px-4 py-2 border border-green-600 text-green-600 rounded-lg text-sm font-medium hover:bg-green-50 disabled:opacity-50 disabled:cursor-not-allowed">
              {testing === 'openai' ? <FiRefreshCw className="animate-spin" /> : <FiZap />}
              <span>{testing === 'openai' ? 'Test en cours...' : 'Tester la connexion'}</span>
            </button>
            {!isOpenaiConfigured && <p className="text-xs text-amber-600">Configurez d'abord la clé API</p>}
          </div>
          {testResult.openai && (
            <div className={`mt-3 flex items-center space-x-2 text-sm rounded-lg px-3 py-2 ${testResult.openai.success ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
              {testResult.openai.success ? <FiCheck /> : <FiAlertCircle />}
              <span>{testResult.openai.success ? `Connexion OK — ${testResult.openai.model} — ${testResult.openai.latencyMs}ms` : testResult.openai.error}</span>
            </div>
          )}
        </div>
      </section>

      {/* ── Routing par tâche ──────────────────────────────────────── */}
      <section className="bg-white rounded-xl border border-gray-200">
        <div className="px-5 py-4 border-b border-gray-100 flex items-center space-x-2">
          <FiSliders className="text-blue-600" />
          <div>
            <h2 className="font-semibold text-gray-900">Routing par tâche</h2>
            <p className="text-xs text-gray-500 mt-0.5">Quel provider et modèle pour chaque type d'appel IA</p>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-100 bg-gray-50">
                <th className="text-left px-5 py-3 font-medium text-gray-600">Tâche</th>
                <th className="text-left px-4 py-3 font-medium text-gray-600">Provider</th>
                <th className="text-left px-4 py-3 font-medium text-gray-600">Modèle</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {ROUTING_TASKS.map(({ key, label, phase }, idx) => {
                const rules = getRoutingRules();
                const rule = rules[key] || { provider: 'claude', model: 'claude-sonnet-4-6' };
                const isClaude = rule.provider === 'claude';
                const prevPhase = idx > 0 ? ROUTING_TASKS[idx - 1].phase : phase;
                const phaseLabels: Record<number, string> = { 1: 'Phase 1 — MVP', 2: 'Phase 2 — Avancé', 3: 'Phase 3 — Enterprise' };
                const phaseColors: Record<number, string> = { 1: 'text-blue-600 bg-blue-50', 2: 'text-purple-600 bg-purple-50', 3: 'text-indigo-600 bg-indigo-50' };
                return (
                  <React.Fragment key={key}>
                    {phase !== prevPhase || idx === 0 ? (
                      <tr>
                        <td colSpan={3} className="px-5 pt-4 pb-1">
                          <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${phaseColors[phase]}`}>
                            {phaseLabels[phase]}
                          </span>
                        </td>
                      </tr>
                    ) : null}
                  <tr className="hover:bg-gray-50">
                    <td className="px-5 py-3 text-gray-700">{label}</td>
                    <td className="px-4 py-3">
                      <select
                        value={rule.provider}
                        onChange={e => updateRoutingRule(key, 'provider', e.target.value)}
                        className={`border rounded-lg px-2 py-1 text-xs font-medium focus:ring-2 ${isClaude ? 'border-blue-200 text-blue-700 bg-blue-50 focus:ring-blue-300' : 'border-green-200 text-green-700 bg-green-50 focus:ring-green-300'}`}
                      >
                        <option value="claude">Claude</option>
                        <option value="openai">OpenAI</option>
                      </select>
                    </td>
                    <td className="px-4 py-3">
                      <select
                        value={rule.model}
                        onChange={e => updateRoutingRule(key, 'model', e.target.value)}
                        className="border border-gray-200 rounded-lg px-2 py-1 text-xs text-gray-700 focus:ring-2 focus:ring-blue-300"
                      >
                        {(isClaude ? CLAUDE_MODELS : OPENAI_MODELS).map(m => (
                          <option key={m.id} value={m.id}>{m.label}</option>
                        ))}
                      </select>
                    </td>
                  </tr>
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>

      {/* ── Phases IA ───────────────────────────────────────────────── */}
      <section className="bg-white rounded-xl border border-gray-200">
        <div className="px-5 py-4 border-b border-gray-100">
          <h2 className="font-semibold text-gray-900">Phases IA</h2>
          <p className="text-xs text-gray-500 mt-0.5">Activer / désactiver les fonctionnalités par phase</p>
        </div>
        <div className="divide-y divide-gray-100">
          {[
            { key: 'phase1_enabled', label: 'Phase 1', desc: 'Génération architecture · Terraform · Documentation · Estimation coûts', badge: 'MVP' },
            { key: 'phase2_enabled', label: 'Phase 2', desc: 'Review architecture · ADR · FinOps · Chat infrastructure', badge: 'Avancé' },
            { key: 'phase3_enabled', label: 'Phase 3', desc: 'Migration assistant · Compliance · Presales · Drift detection', badge: 'Enterprise' },
          ].map(phase => (
            <div key={phase.key} className="px-5 py-4 flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="flex flex-col">
                  <div className="flex items-center space-x-2">
                    <span className="text-sm font-medium text-gray-900">{phase.label}</span>
                    <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">{phase.badge}</span>
                  </div>
                  <p className="text-xs text-gray-500 mt-0.5">{phase.desc}</p>
                </div>
              </div>
              <button
                onClick={() => save(phase.key, !getBool(phase.key))}
                disabled={saving === phase.key}
                className="flex items-center space-x-1.5 text-sm font-medium"
              >
                {getBool(phase.key)
                  ? <><FiToggleRight className="text-blue-600 text-xl" /><span className="text-blue-600">ON</span></>
                  : <><FiToggleLeft className="text-gray-400 text-xl" /><span className="text-gray-400">OFF</span></>
                }
              </button>
            </div>
          ))}
        </div>
      </section>

      {/* ── Quotas ──────────────────────────────────────────────────── */}
      <section className="bg-white rounded-xl border border-gray-200">
        <div className="px-5 py-4 border-b border-gray-100">
          <h2 className="font-semibold text-gray-900">Limites & Alertes</h2>
        </div>
        <div className="divide-y divide-gray-100">
          {[
            { key: 'daily_quota_per_user',     label: 'Quota journalier / utilisateur', unit: 'requêtes/jour' },
            { key: 'cost_alert_threshold_usd', label: 'Alerte coût mensuel',            unit: 'USD' },
          ].map(({ key, label, unit }) => (
            <QuotaRow
              key={key}
              label={label}
              unit={unit}
              value={getVal(key)}
              saving={saving === key}
              onSave={v => save(key, v)}
            />
          ))}
          {/* Fallback toggle */}
          <div className="px-5 py-4 flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-700">Fallback automatique</p>
              <p className="text-xs text-gray-500">Réessayer si le provider est indisponible</p>
            </div>
            <button
              onClick={() => save('fallback_enabled', !getBool('fallback_enabled'))}
              disabled={saving === 'fallback_enabled'}
              className="flex items-center space-x-1.5 text-sm font-medium"
            >
              {getBool('fallback_enabled')
                ? <><FiToggleRight className="text-blue-600 text-xl" /><span className="text-blue-600">ON</span></>
                : <><FiToggleLeft className="text-gray-400 text-xl" /><span className="text-gray-400">OFF</span></>
              }
            </button>
          </div>
        </div>
      </section>

      {/* ── Usage ce mois ───────────────────────────────────────────── */}
      {usage && (
        <section className="bg-white rounded-xl border border-gray-200">
          <div className="px-5 py-4 border-b border-gray-100 flex items-center space-x-2">
            <FiBarChart2 className="text-blue-600" />
            <h2 className="font-semibold text-gray-900">Usage (30 derniers jours)</h2>
          </div>
          <div className="grid grid-cols-3 divide-x divide-gray-100">
            <Stat label="Requêtes"     value={usage.totalRequests.toLocaleString()} />
            <Stat label="Tokens"       value={`${(usage.totalTokens / 1000).toFixed(1)}K`} />
            <Stat label="Coût estimé"  value={`$${usage.totalCostUsd.toFixed(2)}`} />
          </div>
          {usage.breakdown.length > 0 && (
            <div className="px-5 pb-4 pt-2">
              <p className="text-xs text-gray-500 font-medium mb-2">Par type de tâche</p>
              <div className="space-y-1">
                {usage.breakdown.slice(0, 8).map((b, i) => (
                  <div key={i} className="flex items-center justify-between text-xs text-gray-600">
                    <span>{b.task_type}</span>
                    <span className="text-gray-400">{b.total_requests} req · {b.total_tokens?.toLocaleString()} tokens</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </section>
      )}
    </div>
  );
};

const Stat: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div className="px-5 py-4 text-center">
    <p className="text-xl font-bold text-gray-900">{value}</p>
    <p className="text-xs text-gray-500 mt-0.5">{label}</p>
  </div>
);

const QuotaRow: React.FC<{
  label: string; unit: string; value: string;
  saving: boolean; onSave: (v: number) => void;
}> = ({ label, unit, value, saving, onSave }) => {
  const [draft, setDraft] = useState(value);
  useEffect(() => { setDraft(value); }, [value]);

  return (
    <div className="px-5 py-4 flex items-center justify-between">
      <div>
        <p className="text-sm font-medium text-gray-700">{label}</p>
        <p className="text-xs text-gray-500">{unit}</p>
      </div>
      <div className="flex items-center space-x-2">
        <input
          type="number"
          value={draft}
          onChange={e => setDraft(e.target.value)}
          className="w-24 border border-gray-300 rounded-lg px-2 py-1.5 text-sm text-right focus:ring-2 focus:ring-blue-500"
        />
        <button
          onClick={() => onSave(parseInt(draft, 10))}
          disabled={saving || draft === value}
          className="p-1.5 rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-40"
        >
          {saving ? <FiRefreshCw className="animate-spin text-sm" /> : <FiSave className="text-sm" />}
        </button>
      </div>
    </div>
  );
};

export default AdminAIConfig;
